<script lang="ts">
    import { toast, toastIdCounter, visibleToasts } from '$lib/stores';
    import { DateTime } from 'luxon';
    import { onMount } from 'svelte';
    import type { ToastData, ToastOptions } from '$lib/types/Toast.js';
    import IconCircleCheck from '~icons/fa6-solid/circle-check';
    import IconCircleExclamation from '~icons/fa6-solid/circle-exclamation';
    import IconCircleInfo from '~icons/fa6-solid/circle-info';
    import IconTriangleExclamation from '~icons/fa6-solid/triangle-exclamation';
    import { fly } from 'svelte/transition';

    onMount(() => {
        const intervalId = setInterval(updateToasts, 500);
        updateToasts();

        $toast = {
            showToast,
            showSuccess,
            showMessage,
            showInfo,
            showWarning,
            showError
        };

        return () => clearInterval(intervalId);
    });

    function updateToasts() {
        const now = Date.now();

        $visibleToasts = $visibleToasts.filter((t) => now <= t.lifetimeEnd);
    }

    export function showToast(options: Partial<ToastOptions>): void {
        const defaultOptions: Omit<ToastData, 'lifetimeEnd'> = {
            id: $toastIdCounter++,
            message: '',
            duration: {
                millisecond: 2500
            },
            alertClass: '',
            icon: undefined,
            iconClass: '',
            created: DateTime.now()
        };

        $visibleToasts = [
            ...$visibleToasts.filter((t) => t.message !== options.message),
            {
                ...defaultOptions,
                ...options,
                lifetimeEnd: DateTime.now().plus(options.duration || defaultOptions.duration)
            }
        ];
    }

    export function showSuccess(message: string): void {
        showToast({
            message,
            alertClass: 'alert-success',
            icon: IconCircleCheck
        });
    }

    export function showMessage(message: string): void {
        showToast({
            message
        });
    }

    export function showInfo(message: string): void {
        showToast({
            message,
            alertClass: 'alert-info',
            icon: IconCircleInfo
        });
    }

    export function showWarning(message: string): void {
        showToast({
            message,
            alertClass: 'alert-warning',
            icon: IconTriangleExclamation
        });
    }

    export function showError(message: string): void {
        showToast({
            message,
            alertClass: 'alert-error',
            icon: IconCircleExclamation
        });
    }

    function hideToast(toastId: number) {
        $visibleToasts = $visibleToasts.filter((t) => t.id !== toastId);
    }
</script>

<div class="z-50 toast">
    {#each $visibleToasts as toast (toast.id)}
        <div class="flex justify-end">
            <div
                transition:fly={{ x: 50, duration: 200 }}
                class="alert {toast.alertClass} shadow-lg justify-start cursor-pointer hover:scale-[102%] hover:shadow-xl transition-shadow transition-transform"
                on:click={() => hideToast(toast.id)}
            >
                {#if toast.icon}
                    <svelte:component this={toast.icon} class={toast.iconClass} />
                {/if}
                <span>{@html toast.message}</span>
            </div>
        </div>
    {/each}
</div>

<style>
    .alert {
        width: unset;
    }
</style>
